/**
 * Spinner — accessible loading indicator (Claude Design `.spinner`): a ring with
 * a `border-strong` track and a `primary` leading arc that spins.
 *
 * Distinct from the legacy `components/ui.tsx` Spinner (which renders text).
 */
import type { ReactElement } from 'react';

import { cn } from './cn.js';

export type SpinnerSize = 'sm' | 'md' | 'lg';

export interface SpinnerProps {
  size?: SpinnerSize;
  /** Accessible label, announced to screen readers. */
  label?: string;
  className?: string;
}

const sizeClasses: Record<SpinnerSize, string> = {
  sm: 'h-3.5 w-3.5 border-2',
  md: 'h-4 w-4 border-2',
  lg: 'h-6 w-6 border-[3px]',
};

export function Spinner({
  size = 'md',
  label = 'Loading…',
  className,
}: SpinnerProps): ReactElement {
  return (
    <span role="status" aria-live="polite" className={cn('inline-flex', className)}>
      <span
        aria-hidden="true"
        className={cn(
          'inline-block animate-spin rounded-full border-border-strong border-t-primary',
          sizeClasses[size],
        )}
      />
      <span className="sr-only">{label}</span>
    </span>
  );
}
